import { el, clear } from '../../core/dom.js';
import { openModal } from '../../components/modal.js';
import { toast } from '../../components/toast.js';
import { empty } from '../../components/empty.js';
import { errorPanel } from '../../components/error.js';
import { button } from '../../components/button.js';
import { skeleton } from '../../components/skeleton.js';
import { armDestructive, resetDestructive } from './confirm.js';
import {
  activateUser, inviteUser, listUsers, removeUser, resetPassword, updateUser,
} from './actions.js';
import { passwordResetBody } from '../../core/parity.js';
import {
  PAGE_SIZE, accessLabel, initialOf, invitePayload,
  memberUpdatePayload, pageOf, validEmail,
} from './util.js';

/**
 * @param {{
 *   host: HTMLElement,
 *   me: object,
 *   isAdmin: boolean,
 *   products: Array<object>,
 *   dead: () => boolean,
 * }} ctx
 */
export function mountTeam(ctx) {
  const state = {
    rows: [],
    products: ctx.products || [],
    query: '',
    shown: PAGE_SIZE,
    loading: true,
    fatal: '',
    modal: null,
  };

  function paint() {
    if (ctx.dead()) return;
    clear(ctx.host);
    if (state.loading) {
      ctx.host.append(skeleton(6));
      return;
    }
    if (state.fatal) {
      ctx.host.append(errorPanel({
        title: 'Team unavailable',
        message: state.fatal,
        action: button('Try again', { variant: 'primary', onClick: () => load(true) }),
      }));
      return;
    }
    if (!ctx.isAdmin) {
      ctx.host.append(memberSelfView(ctx.me));
      return;
    }
    ctx.host.append(teamPanel(state, {
      onSearch: (value) => { state.query = value; paint(); },
      onMore: () => { state.shown += PAGE_SIZE; paint(); },
      onEdit: (row) => openEdit(row),
      onInvite: () => openInvite(),
    }, ctx));
  }

  function openInvite(prefill) {
    if (state.modal) state.modal.close();
    state.modal = openInviteModal({
      products: state.products,
      prefill,
      onDone: () => load(false),
      onClose: () => { state.modal = null; },
    });
  }

  function openEdit(row) {
    if (state.modal) state.modal.close();
    state.modal = openEditModal({
      row,
      me: ctx.me,
      products: state.products,
      onDone: () => load(false),
      onClose: () => { state.modal = null; },
    });
  }

  async function load(initial) {
    if (initial) {
      state.loading = true;
      state.fatal = '';
      paint();
    }
    if (!ctx.isAdmin) {
      state.loading = false;
      state.fatal = '';
      paint();
      return;
    }
    const data = await listUsers();
    if (ctx.dead()) return;
    state.loading = false;
    if (!data || data.error) {
      state.fatal = (data && data.error) || 'Could not load team';
      state.rows = [];
    } else {
      state.fatal = '';
      state.rows = sortUsers(data.users || []);
    }
    paint();
  }

  paint();
  load(true);
  return {
    openInvite: () => openInvite(),
    refresh: () => load(false),
    setProducts(products) { state.products = products || []; },
    dispose() { if (state.modal) state.modal.close(); },
  };
}

function sortUsers(rows) {
  return [...rows].sort((a, b) => {
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

function memberSelfView(me) {
  return el('div', { class: 'set-stack' },
    empty({
      title: 'Team is admin-managed',
      body: 'Only workspace admins can invite people, change roles, or remove members.',
    }),
    me ? el('div', { class: 'set-note' },
      el('span', { class: 'set-note-kicker' }, 'You'),
      el('p', {}, `${me.name || me.email || 'Member'} · ${me.role === 'admin' ? 'Admin' : 'Member'}`)) : null);
}

function filtered(state) {
  const needle = state.query.trim().toLowerCase();
  if (!needle) return state.rows;
  return state.rows.filter((row) => {
    const hay = `${row.name || ''} ${row.email || ''}`.toLowerCase();
    return hay.includes(needle);
  });
}

function teamPanel(state, handlers, ctx) {
  const all = filtered(state);
  const rows = pageOf(all, state.shown);
  const pending = state.rows.filter((row) => !row.is_active).length;
  return el('div', { class: 'set-stack' },
    el('div', { class: 'set-panel' },
      el('div', { class: 'set-panel-head' },
        el('div', { class: 'set-panel-title' },
          el('span', {}, 'Team'),
          el('span', { class: 'set-count' }, String(state.rows.length)),
          pending ? el('span', { class: 'set-pending' }, `${pending} pending`) : null),
        el('label', { class: 'set-search' },
          el('span', { class: 'sr-only' }, 'Search team'),
          el('input', {
            class: 'set-input',
            type: 'search',
            placeholder: 'Search name or email…',
            value: state.query,
            onInput: (event) => handlers.onSearch(event.target.value || ''),
          }))),
      rows.length
        ? el('div', { class: 'set-team' },
          el('div', { class: 'set-team-cols lbl' },
            el('span', {}, 'Member'),
            el('span', {}, 'Access'),
            el('span', {}, 'Role')),
          rows.map((row) => teamRow(row, state.products, handlers, ctx)))
        : el('div', { class: 'set-panel-empty' },
          empty({
            title: state.query ? 'No matches' : 'No team members yet',
            body: state.query ? 'Try another name or email.' : 'Invite someone to get started.',
            action: ctx.isAdmin
              ? button('Invite Someone', { variant: 'primary', onClick: handlers.onInvite })
              : null,
          })),
      all.length > rows.length
        ? el('div', { class: 'set-panel-foot lbl' },
          el('span', {}, `Showing ${rows.length} of ${all.length}`),
          el('button', { type: 'button', class: 'set-more', onClick: handlers.onMore },
            'View all ', el('span', { class: 'set-arw', 'aria-hidden': 'true' }, '→')))
        : (all.length
          ? el('div', { class: 'set-panel-foot lbl' }, el('span', {}, `Showing ${rows.length} of ${all.length}`))
          : null)),
    el('div', { class: 'set-note' },
      el('span', { class: 'set-note-kicker' }, 'What a new member sees'),
      el('p', {}, 'The product they were given, live connectors, and a short tour. No credential screens — their onboarding is deliberately light.')));
}

function teamRow(row, products, handlers, ctx) {
  const me = ctx.me && ctx.me.id === row.id;
  const role = row.role === 'admin' ? 'Admin' : 'Member';
  const pending = !row.is_active;
  const access = pending
    ? (row.created_at ? `Invited` : 'Invite not accepted yet')
    : accessLabel(row, products);
  return el('div', { class: 'set-team-row' },
    el('span', { class: 'set-member' },
      el('span', {
        class: pending ? 'set-av is-pending' : (me ? 'set-av is-me' : (row.role === 'admin' ? 'set-av is-admin' : 'set-av')),
        'aria-hidden': 'true',
      }, pending ? '?' : initialOf(row.name || row.email)),
      el('span', { class: 'set-member-text' },
        el('span', { class: 'set-member-name' }, `${row.name || row.email || '—'}${me ? ' (you)' : ''}`),
        el('span', { class: 'set-member-email' }, row.email || ''))),
    el('span', { class: 'set-access' }, access),
    el('span', { class: 'set-role-wrap' },
      el('span', {
        class: pending ? 'set-role is-pending' : (row.role === 'admin' ? 'set-role is-admin' : 'set-role'),
      }, pending ? 'Pending' : role),
      ctx.isAdmin && !me
        ? el('button', {
          type: 'button',
          class: 'set-link',
          onClick: () => handlers.onEdit(row),
        }, 'Edit')
        : null));
}

function openInviteModal(opts) {
  const form = { emails: '', role: 'member', access: 'all', productIds: new Set() };
  if (opts.prefill) {
    form.emails = opts.prefill.emails || '';
    form.role = opts.prefill.role === 'admin' ? 'admin' : 'member';
    form.access = opts.prefill.product_access === 'specific' ? 'specific' : 'all';
    (opts.prefill.product_ids || []).forEach((id) => form.productIds.add(id));
  }
  const err = el('p', { class: 'set-form-err', hidden: true });
  const body = el('div', { class: 'set-form' },
    err,
    el('label', { class: 'set-field' },
      el('span', {}, 'Email addresses'),
      el('textarea', {
        class: 'set-textarea',
        rows: 3,
        placeholder: 'name@company.com',
        value: form.emails,
        onInput: (event) => { form.emails = event.target.value; },
      }),
      el('span', { class: 'set-hint' }, 'One address per line, or separate with commas.')),
    roleField(form),
    accessField(form, opts.products),
    el('div', { class: 'set-form-actions' },
      el('button', {
        type: 'button',
        class: 'set-btn set-btn-primary',
        onClick: () => sendInvites(form, err, modal, opts),
      }, 'Send invites')));
  const modal = openModal({
    title: 'Invite someone',
    className: 'set-modal',
    body,
    onClose: opts.onClose,
  });
  return modal;
}

async function sendInvites(form, err, modal, opts) {
  const emails = String(form.emails || '')
    .split(/[\s,;]+/)
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
  err.hidden = true;
  if (!emails.length) {
    err.textContent = 'Enter at least one email address';
    err.hidden = false;
    return;
  }
  const bad = emails.filter((email) => !validEmail(email));
  if (bad.length) {
    err.textContent = `Invalid email${bad.length > 1 ? 's' : ''}: ${bad.join(', ')}`;
    err.hidden = false;
    return;
  }
  if (form.access === 'specific' && !form.productIds.size) {
    err.textContent = 'Pick at least one product, or choose All products';
    err.hidden = false;
    return;
  }
  const failures = [];
  let sent = 0;
  for (const email of emails) {
    const result = await inviteUser(invitePayload({
      email,
      role: form.role,
      access: form.access,
      productIds: form.productIds,
    }));
    if (!result || result.error) failures.push(`${email} — ${(result && result.error) || 'failed'}`);
    else {
      sent += 1;
      if (result.email_sent === false) {
        toast(`Invite created for ${email}, but email failed — copy the link from classic Settings if needed`, 'error');
      }
    }
  }
  if (failures.length) {
    err.textContent = failures.join(' · ');
    err.hidden = false;
  }
  if (sent) {
    toast(sent === 1 ? 'Invite sent' : `${sent} invites sent`);
    if (!failures.length) modal.close();
    if (opts.onDone) await opts.onDone();
  }
}

function openEditModal(opts) {
  const row = opts.row;
  const form = {
    name: row.name || '',
    role: row.role === 'admin' ? 'admin' : 'member',
    access: row.product_access === 'specific' ? 'specific' : 'all',
    productIds: new Set(row.product_ids || []),
    active: !!row.is_active,
  };
  const err = el('p', { class: 'set-form-err', hidden: true });
  const removeBtn = el('button', {
    type: 'button',
    class: 'set-btn set-danger',
    onClick: () => armDestructive(removeBtn, {
      idle: row.is_active ? 'Remove member' : 'Delete invite',
      armed: row.is_active ? 'Remove this member' : 'Delete this invite',
      onConfirm: () => doRemove(row, modal, opts, err, removeBtn),
    }),
  }, row.is_active ? 'Remove member' : 'Delete invite');
  const body = el('div', { class: 'set-form' },
    err,
    el('label', { class: 'set-field' },
      el('span', {}, 'Full name'),
      el('input', {
        class: 'set-input',
        type: 'text',
        value: form.name,
        onInput: (event) => { form.name = event.target.value; },
      })),
    el('label', { class: 'set-field' },
      el('span', {}, 'Email'),
      el('input', { class: 'set-input', type: 'email', value: row.email || '', disabled: true }),
      el('span', { class: 'set-hint' }, 'Email cannot be changed')),
    roleField(form),
    accessField(form, opts.products),
    el('label', { class: 'set-check' },
      el('input', {
        type: 'checkbox',
        checked: form.active ? true : null,
        onChange: (event) => { form.active = !!event.target.checked; },
      }),
      el('span', {}, 'Active (can sign in)')),
    passwordResetField(row, err),
    el('div', { class: 'set-form-actions' },
      removeBtn,
      el('button', {
        type: 'button',
        class: 'set-btn set-btn-primary',
        onClick: () => saveUser(row, form, err, modal, opts, removeBtn),
      }, 'Save changes')));
  const modal = openModal({
    title: 'Edit member',
    className: 'set-modal',
    body,
    onClose: opts.onClose,
  });
  return modal;
}

async function saveUser(row, form, err, modal, opts, removeBtn) {
  resetDestructive(removeBtn, row.is_active ? 'Remove member' : 'Delete invite');
  err.hidden = true;
  if (form.access === 'specific' && !form.productIds.size) {
    err.textContent = 'Pick at least one product, or choose All products';
    err.hidden = false;
    return;
  }
  const body = memberUpdatePayload(form, row);
  let result;
  if (!row.is_active && form.active) {
    result = await activateUser(row.id);
    if (result && !result.error) result = await updateUser(row.id, body);
  } else {
    result = await updateUser(row.id, body);
  }
  if (!result || result.error) {
    err.textContent = (result && result.error) || 'Could not update member';
    err.hidden = false;
    return;
  }
  toast('Member updated');
  modal.close();
  if (opts.onDone) await opts.onDone();
}

async function doRemove(row, modal, opts, err, removeBtn) {
  err.hidden = true;
  removeBtn.disabled = true;
  const result = await removeUser(row.id);
  removeBtn.disabled = false;
  if (!result || result.error) {
    resetDestructive(removeBtn, row.is_active ? 'Remove member' : 'Delete invite');
    err.textContent = (result && result.error) || 'Could not remove member';
    err.hidden = false;
    return;
  }
  toast(row.is_active ? 'Member removed' : 'Invite deleted');
  modal.close();
  if (opts.onDone) await opts.onDone();
}

function passwordResetField(row, err) {
  const input = el('input', {
    class: 'set-input',
    type: 'password',
    autocomplete: 'new-password',
    placeholder: 'Minimum 6 characters',
    'aria-label': 'New password',
  });
  const resetBtn = el('button', {
    type: 'button',
    class: 'set-btn',
    onClick: () => armReset(row, input, err, resetBtn),
  }, 'Reset password');
  input.addEventListener('input', () => resetDestructive(resetBtn, 'Reset password'));
  return el('div', { class: 'set-field' },
    el('span', {}, 'Reset password'),
    input,
    resetBtn);
}

function armReset(row, input, err, resetBtn) {
  const parsed = passwordResetBody(input.value);
  err.hidden = true;
  if (!parsed.ok) {
    err.textContent = parsed.error;
    err.hidden = false;
    resetDestructive(resetBtn, 'Reset password');
    return;
  }
  armDestructive(resetBtn, {
    idle: 'Reset password',
    armed: 'Confirm password reset',
    onConfirm: () => submitReset(row.id, input, err, resetBtn),
  });
}

async function submitReset(id, input, err, resetBtn) {
  const parsed = passwordResetBody(input.value);
  if (!parsed.ok) {
    err.textContent = parsed.error;
    err.hidden = false;
    resetDestructive(resetBtn, 'Reset password');
    return;
  }
  resetBtn.disabled = true;
  const result = await resetPassword(id, parsed.body);
  input.value = '';
  resetBtn.disabled = false;
  resetDestructive(resetBtn, 'Reset password');
  if (!result || result.error) {
    err.textContent = (result && result.error) || 'Could not reset the password';
    err.hidden = false;
    return;
  }
  toast('Password reset');
}

function roleField(form) {
  return el('fieldset', { class: 'set-field' },
    el('legend', {}, 'Role'),
    el('label', { class: 'set-radio' },
      el('input', {
        type: 'radio',
        name: 'set-role',
        checked: form.role === 'admin' ? true : null,
        onChange: () => { form.role = 'admin'; },
      }),
      el('span', {},
        el('strong', {}, 'Admin'),
        el('span', { class: 'set-hint' }, 'Adds products, manages credentials, invites people.'))),
    el('label', { class: 'set-radio' },
      el('input', {
        type: 'radio',
        name: 'set-role',
        checked: form.role !== 'admin' ? true : null,
        onChange: () => { form.role = 'member'; },
      }),
      el('span', {},
        el('strong', {}, 'Member'),
        el('span', { class: 'set-hint' }, 'Uses connectors and chat. Cannot see or edit credentials.'))));
}

function accessField(form, products) {
  const list = el('div', { class: 'set-product-picks', hidden: form.access !== 'specific' });
  (products || []).forEach((product) => {
    list.append(el('label', { class: 'set-check' },
      el('input', {
        type: 'checkbox',
        checked: form.productIds.has(product.id) ? true : null,
        onChange: (event) => {
          if (event.target.checked) form.productIds.add(product.id);
          else form.productIds.delete(product.id);
        },
      }),
      el('span', {}, product.name || product.slug || product.id)));
  });
  if (!(products || []).length) {
    list.append(el('p', { class: 'set-hint' }, 'No products yet.'));
  }
  function sync() {
    list.hidden = form.access !== 'specific';
  }
  return el('fieldset', { class: 'set-field' },
    el('legend', {}, 'Product access'),
    el('label', { class: 'set-radio' },
      el('input', {
        type: 'radio',
        name: 'set-access',
        checked: form.access !== 'specific' ? true : null,
        onChange: () => { form.access = 'all'; sync(); },
      }),
      el('span', {},
        el('strong', {}, 'All products'),
        el('span', { class: 'set-hint' }, 'Includes products added later — no re-inviting'))),
    el('label', { class: 'set-radio' },
      el('input', {
        type: 'radio',
        name: 'set-access',
        checked: form.access === 'specific' ? true : null,
        onChange: () => { form.access = 'specific'; sync(); },
      }),
      el('span', {}, el('strong', {}, 'Specific products'))),
    list);
}
